import { useApp } from "@/AppProvider"
import {
  FilterView,
  useCreateFilterView,
  useDeleteFilterView,
  useUpdateFilterView,
} from "@/hooks/useFilterViews"
import { FC } from "react"
import { SubmitHandler, useForm } from "react-hook-form"
import { VariableFilter } from "./Filters"

interface FilterViewFormProps {
  view?: FilterView | null
  viewName: string
  filters: VariableFilter[]
  organizationId: string
  onClose: () => void
}

interface FilterViewInput {
  name: string
}

export const FilterViewForm: FC<FilterViewFormProps> = ({
  view,
  filters,
  organizationId,
  viewName,
  onClose,
}) => {
  const { token, addToast } = useApp()

  const isEdit = !!view
  const {
    register,
    handleSubmit,
    formState: { isDirty },
    reset,
  } = useForm<FilterViewInput>({
    defaultValues: { name: view?.name || "" },
  })
  const createMutation = useCreateFilterView(token)
  const updateMutation = useUpdateFilterView(organizationId, viewName, token)
  const deleteMutation = useDeleteFilterView(organizationId, viewName, token)
  const mutation = isEdit ? updateMutation : createMutation

  const onSubmit: SubmitHandler<FilterViewInput> = async (data) => {
    try {
      if (isEdit && view) {
        await updateMutation.mutateAsync({
          id: view._id,
          name: data.name,
          filters,
        })
      } else {
        await createMutation.mutateAsync({
          name: data.name,
          filters,
          organizationId,
          table: viewName,
        })
      }
      reset()
      onClose()
    } catch (error) {
      addToast("Server error", (error as Error).message, "error")
    }
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <label className="label">
        Name
        <input
          {...register("name", { required: true })}
          type="text"
          placeholder="View name"
          className="input"
          autoFocus
        />
      </label>
      <div className="mt-4">
        <div className="label">Filters</div>
        {filters.length === 0 && <div className="text-gray-400">No filters selected.</div>}
        {filters.map((filter, idx) => (
          <div key={idx} className="mb-2 flex items-center gap-2">
            <span className="text-gray-700">{filter.key}</span>
            <span className="text-gray-500">{filter.operator}</span>
            <span className="text-gray-700">{filter.value}</span>
          </div>
        ))}
      </div>
      <div className="mt-6 flex items-center justify-between gap-x-2">
        {isEdit ? (
          <button
            type="button"
            className="btn-danger"
            onClick={async () => {
              if (!view) return
              try {
                await deleteMutation.mutateAsync(view._id)
                reset()
                onClose()
              } catch (error) {
                addToast("Server error", (error as Error).message, "error")
              }
            }}
            disabled={deleteMutation.isPending}
          >
            {deleteMutation.isPending ? "Deleting..." : "Delete"}
          </button>
        ) : (
          <span />
        )}
        <div className="flex gap-x-2">
          <button
            type="button"
            className="btn-secondary"
            onClick={() => {
              reset()
              onClose()
            }}
            disabled={mutation.isPending}
          >
            Cancel
          </button>
          <button type="submit" className="btn" disabled={mutation.isPending}>
            {mutation.isPending ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </form>
  )
}
